<script setup lang="ts">
// 답장 다듬기(design/compose.js 포팅 + AI 연결). 이름은 ai.ts가 마스킹해 보내고 되돌린다.
// 엔진이 없거나 느리면 간단 모드(규칙 체크리스트)로 떨어진다.
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVaultStore } from '../../stores/vault'
import { aiReply, type ReplyResult, type Seg } from '../../lib/ai'
import { simpleChecklist, type Situation } from '../../lib/simpleMode'
import { icon } from '../../lib/icons'

const props = defineProps<{ personId: string; prefill?: string }>()
const emit = defineEmits<{ toast: [msg: string]; good: [id: string] }>()
const { t, locale } = useI18n()
const vault = useVaultStore()

const person = computed(() => vault.people.find(p => p.id === props.personId))
const channel = ref<'chat' | 'mail'>('chat')
const SITS: Situation[] = ['status_late', 'ask_help', 'say_no', 'nudge', 'apology', 'thanks']
const situation = ref<Situation>('status_late')
const incoming = ref('')
const draft = ref('')
const busy = ref(false)
const result = ref<ReplyResult | null>(null)
const checklist = ref<string[] | null>(null)
const flash = ref('')

const loc = () => (locale.value === 'en' ? 'en' : 'ko') as 'ko' | 'en'

// 일기장 "이 대화로 답장 쓰기"에서 받은 메시지를 채운다
watch(() => props.prefill, (v) => { if (v) { incoming.value = v; result.value = null; checklist.value = null } })

async function convert() {
  if (busy.value || !draft.value.trim() || !person.value) return
  busy.value = true; result.value = null; checklist.value = null
  const others = vault.people.filter(p => p.id !== props.personId)
  const people = [person.value, ...others].map(p => ({ name: p.name, aliases: p.aliases }))
  const r = await aiReply({
    people, relation: person.value.rel || '', situation: situation.value, channel: channel.value,
    incoming: incoming.value, draft: draft.value, uiLocale: loc(), replyLocale: loc(),
    recipientDo: person.value.dos || [], recipientDont: person.value.donts || [], recipientTip: person.value.tip || '',
  })
  if (r) result.value = r
  else checklist.value = simpleChecklist(draft.value, { channel: channel.value, situation: situation.value, locale: loc() })
  busy.value = false
}

function segText(segs: Seg[]) { return segs.map(s => s.t).join('') }
function segCls(s: Seg, vi: number) {
  if (!s.hl) return 'cp-seg'
  return ['cp-seg', 'hl', 'on', flash.value === `${vi}-${s.hl}` ? 'flash' : ''].join(' ')
}
function copyVariant(v: ReplyResult['variants'][number]) {
  let text = ''
  if (v.bubbles) text = v.bubbles.map(b => segText(b)).join('\n')
  else if (v.mail) text = [t('compose.kindMail') + ': ' + segText(v.mail.subject), ...v.mail.body.map(p => segText(p))].join('\n')
  navigator.clipboard?.writeText(text).catch(() => {})
  emit('toast', t('compose.copied'))
}
function flashReason(vi: number, hl: number) { flash.value = `${vi}-${hl}`; setTimeout(() => (flash.value = ''), 600) }
function again() { result.value = null; checklist.value = null }
function good() { emit('good', props.personId) }
</script>

<template>
  <div v-if="person" class="cp">
    <div class="cp-ch">
      <button :class="{ sel: channel === 'chat' }" @click="channel = 'chat'; again()"><span v-html="icon('chat')"></span>{{ t('compose.chat') }}</button>
      <button :class="{ sel: channel === 'mail' }" @click="channel = 'mail'; again()"><span v-html="icon('mail')"></span>{{ t('compose.mail') }}</button>
    </div>

    <div class="cp-lbl">{{ t('compose.situation') }}</div>
    <div class="cp-sits">
      <button v-for="s in SITS" :key="s" :class="{ sel: situation === s }" @click="situation = s">{{ t('compose.sit_' + s) }}</button>
    </div>

    <div class="cp-lbl">{{ t('compose.incoming') }}</div>
    <textarea class="cp-ta" v-model="incoming" :placeholder="t('compose.incomingPlaceholder')" rows="2"></textarea>

    <div class="cp-card cp-draft">
      <div class="cp-lbl">{{ t('compose.draft') }}<small>{{ t('compose.draftHint') }}</small></div>
      <textarea v-model="draft" :placeholder="t('compose.draftPlaceholder')" aria-label="draft"></textarea>
    </div>

    <button class="cp-go" :disabled="busy || !draft.trim()" @click="convert">
      <template v-if="busy">{{ t('compose.thinking') }} <i class="cp-dots">···</i></template>
      <template v-else>{{ t('compose.go') }}</template>
    </button>

    <!-- 로딩 스켈레톤 — 말결 고르는 동안 빈 화면 대신 부드럽게 채운다 -->
    <div v-if="busy" class="cp-card cp-out show cp-thinking" aria-live="polite">
      <div class="cp-lbl">{{ t('compose.thinking') }} <i class="cp-dots">···</i></div>
      <div v-if="incoming.trim() && channel === 'chat'" class="cp-thread them-thread"><div class="cp-b them">{{ incoming }}</div></div>
      <div v-if="channel === 'chat'" class="cp-thread mine">
        <div class="cp-b me skel" style="width:72%"></div>
        <div class="cp-b me skel" style="width:54%"></div>
        <div class="cp-b me skel" style="width:63%"></div>
      </div>
      <div v-else class="cp-skel-mail">
        <div class="skel skel-line" style="width:45%"></div>
        <div class="skel skel-line" style="width:92%"></div>
        <div class="skel skel-line" style="width:88%"></div>
        <div class="skel skel-line" style="width:76%"></div>
      </div>
    </div>

    <!-- AI 결과 -->
    <div v-else-if="result" class="cp-card cp-out show">
      <div class="cp-lbl">{{ t('compose.outFor', { name: person.name, kind: channel === 'chat' ? t('compose.kindChat') : t('compose.kindMail') }) }}</div>
      <!-- 받은 메시지를 상대 말풍선으로 보여 대화처럼 — 내 다듬은 답장이 그에 대한 답으로 읽힌다 -->
      <div v-if="incoming.trim() && channel === 'chat'" class="cp-thread them-thread"><div class="cp-b them">{{ incoming }}</div></div>
      <div v-for="(v, vi) in result.variants" :key="vi" class="cp-variant">
        <div class="cp-vlabel">{{ v.label }}</div>
        <div v-if="v.bubbles" class="cp-thread mine">
          <div v-for="(b, bi) in v.bubbles" :key="bi" class="cp-b me">
            <span v-for="(s, si) in b" :key="si" :class="segCls(s, vi)">{{ s.t }}<sup v-if="s.hl">{{ s.hl }}</sup></span>
          </div>
        </div>
        <div v-else-if="v.mail" class="cp-mail out">
          <div class="cp-subj"><small>{{ t('compose.kindMail') }}</small><span v-for="(s, si) in v.mail.subject" :key="si" :class="segCls(s, vi)">{{ s.t }}<sup v-if="s.hl">{{ s.hl }}</sup></span></div>
          <div class="cp-body"><p v-for="(para, pi) in v.mail.body" :key="pi"><span v-for="(s, si) in para" :key="si" :class="segCls(s, vi)">{{ s.t }}<sup v-if="s.hl">{{ s.hl }}</sup></span></p></div>
        </div>
        <div class="cp-reasons">
          <button v-for="(r, ri) in v.reasons" :key="ri" class="cp-reason" @click="flashReason(vi, ri + 1)"><b>{{ ri + 1 }}</b>{{ r }}</button>
        </div>
        <div v-if="v.missing.length" class="cp-note">{{ t('compose.missing') }}: {{ v.missing.join(', ') }}</div>
        <div class="cp-act on"><button class="good" @click="good">{{ t('compose.good') }}</button><button @click="copyVariant(v)">{{ t('compose.copy') }}</button></div>
      </div>
      <div class="cp-note">{{ t('compose.engineNote', { engine: result.engine }) }}</div>
      <button class="cp-again" @click="again">{{ t('compose.again') }}</button>
    </div>

    <!-- 간단 모드 -->
    <div v-else-if="checklist" class="cp-card cp-out show">
      <div class="cp-lbl">{{ t('compose.simpleTitle') }}</div>
      <ul v-if="checklist.length" class="cp-check"><li v-for="id in checklist" :key="id">{{ t('simple.d_' + id) }}</li></ul>
      <p v-else class="cp-note">{{ t('compose.allGood') }}</p>
      <p class="cp-note">{{ t('compose.simpleNote') }}</p>
      <button class="cp-again" @click="again">{{ t('compose.again') }}</button>
    </div>
  </div>
</template>
